import publicUrls from "../../../routes/publicUrls";
import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

import { auction } from "../../../Model/home";
import { encodeId } from "../../../hash";
import Section from "../../../components/gallery/Section";
import SectionHeader, { ArrowLink } from "../../../components/gallery/SectionHeader";
import CardRail from "../../../components/gallery/CardRail";
import { EventCard } from "../../../components/gallery/Cards";
import { formatLongDate, storage } from "../utils";



// Same fallback image URL the previous version used when an auction has no cover.
const PLACEHOLDER_IMAGE = "https://placehold.co/400x500?text=No+Image+Available";

const TYPE_LABELS = {
  LIVE_AUCTION: "Live auction",
  ONLINE_AUCTION: "Online auction",
  TIME_AUCTION: "Timed auction",
  EXHIBITION: "Exhibition",
};

function typeLabel(type) {
  if (!type) return "";
  return TYPE_LABELS[type] || type.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

// Unchanged link rules.
function getAuctionUrl({ id, c_slug, c_type, d_name }) {
  if (c_type === "EXHIBITION") {
    return publicUrls.exhibition(d_name || c_type || "exhibition", id);
  }
  return publicUrls.auction(c_slug, encodeId(id));
}

// Unchanged: remembers the selection for the auction page.
function rememberSelection({ c_type, cover }) {
  if (c_type) storage.set("auctionType", String(c_type));
  if (cover) storage.set("cover", cover);
  else storage.remove("cover");
}

function AuctionAndExhibition() {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        const results = await auction();
        if (isActive) setAuctions(Array.isArray(results) ? results : []);
      } catch (error) {
        console.error("Failed to load auctions:", error);
      } finally {
        if (isActive) setLoading(false);
      }
    })();
    return () => {
      isActive = false;
    };
  }, []);

  return (
    <Section tone="ivory" labelledBy="home-auctions-title">
      <SectionHeader
        id="home-auctions-title"
        eyebrow="Auctions"
        title="Current & upcoming auctions"
        actionLabel="View all auctions"
        actionHref="/auctions"
      />

      {loading ? (
        <Box role="status" aria-live="polite" sx={{ display: "flex", alignItems: "center", gap: 2, py: 8 }}>
          <CircularProgress size={24} color="inherit" />
          <Typography variant="body2">Loading auctions…</Typography>
        </Box>
      ) : auctions.length === 0 ? (
        <Box sx={{ py: 6, display: "grid", gap: 2, justifyItems: "start" }}>
          <Typography variant="body1">There are no auctions to show right now.</Typography>
          <ArrowLink href="/auctions">See the auction calendar</ArrowLink>
        </Box>
      ) : (
        <CardRail label="auctions" perView={{ sm: 2, md: 3, lg: 4 }} mobileWidth="44%">
          {auctions.map((item) => (
            <EventCard
              key={item.id}
              href={getAuctionUrl(item)}
              onClick={() => rememberSelection(item)}
              image={item.cover || PLACEHOLDER_IMAGE}
              imageAlt={item.d_name ? `${item.d_name} — catalogue cover` : "Auction catalogue cover"}
              eyebrow={typeLabel(item.c_type)}
              title={item.d_name}
              meta={formatLongDate(item.date_start)}
              compact
            />
          ))}
        </CardRail>
      )}
    </Section>
  );
}

export default React.memo(AuctionAndExhibition);
